import React, { useState } from 'react';
import { X, Upload, Download, AlertCircle, CheckCircle, Users } from 'lucide-react';
import { dbAuthService } from '@/services/dbAuthService';
import type { BulkUserCreateResponse, UserRole } from '@/types/dbAuth.types';

interface BulkUserImportProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const BulkUserImport: React.FC<BulkUserImportProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [emailsText, setEmailsText] = useState('');
  const [passwordMode, setPasswordMode] = useState<'shared' | 'generate'>('generate');
  const [sharedPassword, setSharedPassword] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [forcePasswordChange, setForcePasswordChange] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<BulkUserCreateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setResult(null);

    try {
      // Parse emails from textarea (split by newline, comma, or semicolon)
      const emails = emailsText
        .split(/[\n,;]+/)
        .map((email) => email.trim())
        .filter((email) => email.length > 0);

      if (emails.length === 0) {
        setError('Please enter at least one email address');
        setIsSubmitting(false);
        return;
      }

      // Validate shared password if required
      if (passwordMode === 'shared' && !sharedPassword) {
        setError('Please enter a shared password');
        setIsSubmitting(false);
        return;
      }

      // Call API
      const response = await dbAuthService.createBulkUsers({
        emails,
        sharedPassword: passwordMode === 'shared' ? sharedPassword : undefined,
        generatePasswords: passwordMode === 'generate',
        role,
        forcePasswordChange,
      });

      setResult(response);

      // If all successful, close modal after 2 seconds
      if (response.failed.length === 0) {
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create users');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmailsText('');
    setSharedPassword('');
    setPasswordMode('generate');
    setRole('user');
    setForcePasswordChange(true);
    setResult(null);
    setError(null);
    onClose();
  };

  const handleDownloadResults = () => {
    if (!result) return;

    // CWE-80 mitigation: sanitize every CSV cell INLINE with a strict
    // character whitelist BEFORE joining. Anything outside the whitelist
    // (including formula-trigger characters =,+,-,@, tabs, newlines, and
    // quotes) is stripped, so the output cannot smuggle Excel formulas,
    // line-break injections, or quote-escape attacks.
    const csvCell = (raw: string | null | undefined): string => {
      if (raw == null) return '';
      return String(raw).replace(/[^A-Za-z0-9 ._@-]/g, '');
    };

    const csvLines = [
      'Email,Username,Password,Status,Error',
      ...result.successful.map(
        (user) =>
          [csvCell(user.email), csvCell(user.username), csvCell(user.password), 'Success', ''].join(',')
      ),
      ...result.failed.map(
        (fail) =>
          [csvCell(fail.email), '', '', 'Failed', csvCell(fail.error)].join(',')
      ),
    ];

    const csvContent = csvLines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-user-import-${new Date().toISOString()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-bg-primary rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-default">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-stellar-primary" />
            <h2 className="text-2xl font-bold text-text-primary">Bulk User Import</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-bg-secondary rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!result ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email addresses */}
              <div>
                <label htmlFor="emails" className="block text-sm font-semibold text-text-primary mb-2">
                  Email Addresses
                </label>
                <p className="text-sm text-text-secondary mb-2">
                  Enter email addresses (one per line, or separated by commas/semicolons)
                </p>
                <textarea
                  id="emails"
                  value={emailsText}
                  onChange={(e) => setEmailsText(e.target.value)}
                  className="w-full h-40 px-4 py-2 bg-bg-secondary border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:border-transparent resize-none font-mono text-sm"
                  placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                  required
                />
              </div>

              {/* Password mode */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Password Configuration
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 border border-border-default rounded-lg cursor-pointer hover:bg-bg-secondary transition-colors">
                    <input
                      type="radio"
                      name="passwordMode"
                      value="generate"
                      checked={passwordMode === 'generate'}
                      onChange={(e) => setPasswordMode(e.target.value as 'shared' | 'generate')}
                      className="w-4 h-4 text-stellar-primary"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-text-primary">Generate Random Passwords</div>
                      <div className="text-sm text-text-secondary">
                        Each user will receive a unique secure password
                      </div>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 border border-border-default rounded-lg cursor-pointer hover:bg-bg-secondary transition-colors">
                    <input
                      type="radio"
                      name="passwordMode"
                      value="shared"
                      checked={passwordMode === 'shared'}
                      onChange={(e) => setPasswordMode(e.target.value as 'shared' | 'generate')}
                      className="w-4 h-4 text-stellar-primary"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-text-primary">Use Shared Password</div>
                      <div className="text-sm text-text-secondary">
                        All users will have the same password
                      </div>
                    </div>
                  </label>
                </div>

                {/* Shared password input */}
                {passwordMode === 'shared' && (
                  <div className="mt-3">
                    <input
                      type="password"
                      value={sharedPassword}
                      onChange={(e) => setSharedPassword(e.target.value)}
                      className="w-full px-4 py-2 bg-bg-secondary border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:border-transparent"
                      placeholder="Enter shared password (min 8 chars, uppercase, lowercase, number)"
                      required={passwordMode === 'shared'}
                    />
                  </div>
                )}
              </div>

              {/* Role selection */}
              <div>
                <label htmlFor="role" className="block text-sm font-semibold text-text-primary mb-2">
                  User Role
                </label>
                <select
                  id="role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as UserRole)}
                  className="w-full px-4 py-2 bg-bg-secondary border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:border-transparent"
                >
                  <option value="user">User</option>
                  <option value="power_user">Power User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Force password change */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={forcePasswordChange}
                    onChange={(e) => setForcePasswordChange(e.target.checked)}
                    className="w-4 h-4 text-primary rounded"
                  />
                  <div>
                    <div className="font-medium text-text-primary">Force Password Change</div>
                    <div className="text-sm text-text-secondary">
                      Users must change their password on first login
                    </div>
                  </div>
                </label>
              </div>

              {/* Error message */}
              {error && (
                <div className="flex items-start gap-3 p-4 bg-error-light border border-error rounded-lg">
                  <AlertCircle className="h-5 w-5 text-error flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-error">Error</p>
                    <p className="text-sm text-error mt-1">{error}</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-default">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-text-secondary hover:bg-bg-secondary rounded-lg transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-6 py-2 bg-stellar-primary text-white rounded-lg hover:bg-stellar-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  {isSubmitting ? 'Creating Users...' : 'Create Users'}
                </button>
              </div>
            </form>
          ) : (
            /* Results */
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-success-light border border-success rounded-lg">
                  <div className="flex items-center gap-2 text-success mb-2">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-semibold">Successful</span>
                  </div>
                  <div className="text-3xl font-bold text-success">
                    {result.successful.length}
                  </div>
                </div>

                <div className="p-4 bg-error-light border border-error rounded-lg">
                  <div className="flex items-center gap-2 text-error mb-2">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-semibold">Failed</span>
                  </div>
                  <div className="text-3xl font-bold text-error">
                    {result.failed.length}
                  </div>
                </div>
              </div>

              {/* Successful users */}
              {result.successful.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-3">
                    Successfully Created ({result.successful.length})
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {result.successful.map((user, index) => (
                      <div
                        key={index}
                        className="p-3 bg-bg-secondary border border-border-default rounded-lg"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-text-primary">{user.email}</div>
                            <div className="text-sm text-text-secondary">
                              Username: <span className="font-mono">{user.username}</span>
                            </div>
                            <div className="text-sm text-text-secondary">
                              Password: <span className="font-mono">{user.password}</span>
                            </div>
                          </div>
                          <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Failed users */}
              {result.failed.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-3">
                    Failed ({result.failed.length})
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {result.failed.map((fail, index) => (
                      <div
                        key={index}
                        className="p-3 bg-bg-secondary border border-border-default rounded-lg"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-text-primary">{fail.email}</div>
                            <div className="text-sm text-error">{fail.error}</div>
                          </div>
                          <AlertCircle className="h-5 w-5 text-error flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-default">
                <button
                  onClick={handleDownloadResults}
                  className="flex items-center gap-2 px-4 py-2 text-text-secondary hover:bg-bg-secondary rounded-lg transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Download CSV
                </button>
                <button
                  onClick={handleClose}
                  className="px-6 py-2 bg-stellar-primary text-white rounded-lg hover:bg-stellar-primary-dark transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
